import React, { useState } from 'react';
import { MapPin, Users, Leaf } from 'lucide-react';
import { mockData } from '../utils/mockData';

const BrazilMap = () => {
  const [activeMarker, setActiveMarker] = useState(null);

  // Coordenadas ajustadas para centralizar nos estados coloridos
  const markers = [
    {
      id: 0,
      x: '68%', // Bahia - Amarelo (nordeste)
      y: '46%',
      territory: mockData.territories[0],
      state: 'BA'
    },
    {
      id: 1,
      x: '52.5%', // Amapá - Verde (extremo norte)
      y: '18%',
      territory: mockData.territories[1],
      state: 'AP'
    },
    {
      id: 2,
      x: '52.5%', // Paraná - Verde (sul)
      y: '73.5%',
      territory: mockData.territories[2],
      state: 'PR'
    },
    {
      id: 3,
      x: '50%', // Pará - Azul (norte-centro)
      y: '29%',
      territory: mockData.territories[3],
      state: 'PA'
    },
    {
      id: 4,
      x: '28%', // Acre - Azul (oeste)
      y: '40%',
      territory: mockData.territories[3],
      state: 'AC'
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-8 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl font-bold text-ecam-forest mb-4">
            Territórios do Carbono Social
          </h2>
          <p className="text-lg md:text-xl text-gray-700">Conectando o Brasil através da regeneração</p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white p-6 rounded-2xl shadow-lg">
            <h3 className="text-xl md:text-2xl font-bold text-ecam-forest text-center mb-6">Projetos já em execução</h3>
            
            {/* Container do Mapa - altura fixa e responsiva */}
            <div className="relative w-full h-[400px] sm:h-[500px] md:h-[600px] overflow-hidden flex items-center justify-center">
              {/* Mapa SVG do Brasil (mantém proporção dentro da box) */}
              <img
                src="/images/svg/Mapa_Brasil_nomeado.svg"
                alt="Mapa do Brasil"
                className="max-w-full h-auto object-contain"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '100%',
                }}
              />

              {/* Marcadores Interativos */}
              {markers.map((marker) => (
                <div
                  key={marker.id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
                  style={{ 
                    left: marker.x, 
                    top: marker.y
                  }}
                  onMouseEnter={() => setActiveMarker(marker.id)}
                  onMouseLeave={() => setActiveMarker(null)}
                >
                  {/* Pin do Marcador */}
                  <div className="relative">
                    <div 
                      className={`w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 ${
                        activeMarker === marker.id 
                          ? 'bg-ecam-red scale-125 shadow-2xl' 
                          : 'bg-ecam-blue hover:bg-ecam-celeste shadow-lg'
                      }`}
                    >
                      <MapPin className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 text-white" />
                    </div>

                    {/* Efeito pulsante */}
                    <div 
                      className={`absolute inset-0 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full animate-ping ${
                        activeMarker === marker.id ? 'bg-ecam-red' : 'bg-ecam-blue'
                      }`} 
                      style={{ opacity: 0.3 }}
                    />

                    {/* Tooltip */}
                    {activeMarker === marker.id && (
                      <div 
                        className="hidden md:block absolute left-14 top-0 bg-white rounded-xl shadow-2xl p-5 w-72 border-2 border-ecam-blue animate-fade-up z-20"
                        style={{ animationDuration: '0.3s' }}
                      >
                        {/* Setinha */}
                        <div className="absolute left-0 top-4 transform -translate-x-1/2 rotate-45 w-3 h-3 bg-white border-l-2 border-b-2 border-ecam-blue"></div>
                        
                        <div className="flex items-start gap-2 mb-2">
                          <div className="bg-ecam-blue/10 p-2 rounded-lg">
                            <MapPin className="h-4 w-4 text-ecam-blue" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-ecam-forest text-base leading-tight">
                              {marker.state === 'AC' ? 'Acre' : marker.state === 'PA' ? 'Pará' : marker.territory.region}
                            </h4>
                            <p className="text-xs text-gray-500">{marker.state}</p>
                          </div>
                        </div>

                        <h5 className="font-bold text-ecam-blue text-sm mb-2">{marker.territory.name}</h5>
                        <p className="text-xs text-gray-600 mb-3 leading-relaxed">{marker.territory.description}</p>
                        
                        <div className="border-t border-gray-200 pt-2 mt-2">
                          <p className="text-xs text-gray-500 mb-1">{marker.territory.partner}</p>
                          {marker.territory.project && (
                            <p className="text-xs text-gray-400 italic mb-2">{marker.territory.project}</p>
                          )}
                          <div className="flex gap-3 text-xs">
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3 text-ecam-green" />
                              <span className="font-semibold text-ecam-green">{marker.territory.families}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Leaf className="h-3 w-3 text-ecam-blue" />
                              <span className="font-semibold text-ecam-blue">{marker.territory.carbon}</span>
                            </div>
                          </div>
                          {marker.territory.carbonNote && (
                            <p className="text-xs text-gray-400 italic mt-1">{marker.territory.carbonNote}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Legenda / Cards */}
            <div className="mt-6">
              {/* Desktop */}
              <div className="hidden md:flex flex-wrap justify-center gap-4">
                {['Bahia', 'Amapá', 'Paraná', 'Pará', 'Acre'].map((state, i) => (
                  <div 
                    key={i}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-ecam-blue/10 transition-colors cursor-pointer"
                    onMouseEnter={() => setActiveMarker(i)}
                    onMouseLeave={() => setActiveMarker(null)}
                  >
                    <div className={`w-2 h-2 rounded-full ${activeMarker === i ? 'bg-ecam-red' : 'bg-ecam-blue'}`}></div>
                    <span className="text-sm font-semibold text-gray-700">{state}</span>
                  </div>
                ))}
              </div>

              {/* Mobile */}
              <div className="md:hidden space-y-3">
                {mockData.territories.map((territory, index) => (
                  <div 
                    key={index}
                    className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-start gap-3 mb-2">
                      <div className="bg-ecam-blue w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-ecam-forest text-sm">{territory.region}</h4>
                        <p className="text-xs text-ecam-blue font-semibold">{territory.name}</p>
                      </div>
                    </div>
                    <div className="flex gap-3 text-xs">
                      <span className="text-ecam-green font-semibold">{territory.families}</span>
                      <span className="text-ecam-blue font-semibold">{territory.carbon}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default BrazilMap;
